import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, LogOut, Search, Filter, 
  ChevronDown, ChevronUp, RefreshCw, Download, CheckSquare,
  Clock, Send, CheckCircle, AlertCircle, X, Eye, Edit2,
  BarChart3, TrendingUp, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useNoIndex } from '../lib/seoHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  submitted: { label: 'Submitted', color: 'bg-blue-500', textColor: 'text-blue-400' },
  under_review: { label: 'Under Review', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  documentation_in_progress: { label: 'Doc In Progress', color: 'bg-orange-500', textColor: 'text-orange-400' },
  documentation_complete: { label: 'Doc Complete', color: 'bg-lime-500', textColor: 'text-lime-400' },
  sent_to_platform: { label: 'Sent to Platform', color: 'bg-purple-500', textColor: 'text-purple-400' },
  awaiting_response: { label: 'Awaiting Response', color: 'bg-indigo-500', textColor: 'text-indigo-400' },
  response_received: { label: 'Response Received', color: 'bg-cyan-500', textColor: 'text-cyan-400' },
  resolved: { label: 'Resolved', color: 'bg-green-500', textColor: 'text-green-400' },
  denied: { label: 'Denied', color: 'bg-red-500', textColor: 'text-red-400' },
  closed: { label: 'Closed', color: 'bg-gray-500', textColor: 'text-gray-400' }
};

const PLATFORMS = ['Instagram', 'Facebook', 'TikTok', 'YouTube', 'X (Twitter)', 'Reddit', 'Snapchat', 'LinkedIn', 'Other'];

const AdminDashboardPage = () => {
  useNoIndex();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, pages: 1 });
  const [selectedCases, setSelectedCases] = useState([]);
  const [filters, setFilters] = useState({ status: '', platform: '', search: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'createdAt', order: 'desc' });
  const [showFilters, setShowFilters] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [adminUser, setAdminUser] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('sdc_admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem('sdc_admin_token');
    const user = localStorage.getItem('sdc_admin_user');
    
    if (!token) {
      navigate('/admin');
      return;
    }
    
    if (user) {
      setAdminUser(JSON.parse(user));
    }
    
    // Verify token
    axios.get(`${API}/admin/me`, { headers: getAuthHeaders() })
      .then(res => setAdminUser(res.data))
      .catch(() => {
        localStorage.removeItem('sdc_admin_token');
        localStorage.removeItem('sdc_admin_user');
        navigate('/admin');
      });
  }, [navigate, getAuthHeaders]);

  // Fetch cases
  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sort_by: sortConfig.field,
        sort_order: sortConfig.order
      });
      
      if (filters.status) params.append('status', filters.status);
      if (filters.platform) params.append('platform', filters.platform);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/admin/cases?${params}`, {
        headers: getAuthHeaders()
      });
      
      setCases(response.data.cases);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin');
      } else {
        toast.error('Failed to fetch cases');
      }
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, sortConfig, filters, getAuthHeaders, navigate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (adminUser) {
      fetchCases();
      fetchStats();
    }
  }, [adminUser, fetchCases, fetchStats]);

  const handleLogout = () => {
    localStorage.removeItem('sdc_admin_token');
    localStorage.removeItem('sdc_admin_user');
    toast.success('Logged out successfully');
    navigate('/admin');
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleSelectAll = () => {
    if (selectedCases.length === cases.length) {
      setSelectedCases([]);
    } else {
      setSelectedCases(cases.map(c => c.caseId));
    }
  };

  const handleSelectCase = (caseId) => {
    setSelectedCases(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (!newStatus || selectedCases.length === 0) {
      toast.error('Select cases and a status');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/cases/bulk-status`, {
        caseIds: selectedCases,
        status: newStatus,
        note: statusNote || null
      }, { headers: getAuthHeaders() });
      
      toast.success(`Updated ${selectedCases.length} cases`);
      setSelectedCases([]);
      setNewStatus('');
      setStatusNote('');
      fetchCases();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update cases');
    }
  };

  const handleUpdateSingleStatus = async (caseId) => {
    if (!newStatus) {
      toast.error('Select a status');
      return;
    }
    
    try {
      await axios.patch(`${API}/admin/cases/${caseId}/status`, {
        status: newStatus,
        note: statusNote || null
      }, { headers: getAuthHeaders() });
      
      toast.success('Status updated');
      setEditingCase(null);
      setNewStatus('');
      setStatusNote('');
      fetchCases();
      fetchStats();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const exportToCSV = () => {
    const headers = ['Case ID', 'Client Name', 'Email', 'Phone', 'Platform', 'Account', 'Status', 'Created'];
    const rows = cases.map(c => [
      c.caseId,
      c.clientName,
      c.email,
      c.phone,
      c.platform,
      c.accountName,
      c.status,
      new Date(c.createdAt).toLocaleDateString()
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sdc-cases-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  if (!adminUser) {
    return (
      <div className="min-h-screen bg-[#1a1c1b] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#d9fb06] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1c1b]">
      {/* Header */}
      <header className="bg-[#302f2c] border-b border-[#3f4816]/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LayoutDashboard className="w-6 h-6 text-[#d9fb06]" />
            <h1 className="text-xl font-bold text-[#d9fb06]">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/admin/reindex"
              className="hidden md:inline-flex text-sm text-[#888680] hover:text-[#d9fb06] font-medium"
              data-testid="admin-nav-reindex"
            >
              Reindex →
            </Link>
            <Link
              to="/admin/referrals"
              className="hidden md:inline-flex text-sm text-[#888680] hover:text-[#d9fb06] font-medium"
              data-testid="admin-nav-referrals"
            >
              Referrals →
            </Link>
            <Link
              to="/admin/backlinks"
              className="hidden md:inline-flex text-sm text-[#888680] hover:text-[#d9fb06] font-medium"
              data-testid="admin-nav-backlinks"
            >
              Backlink Center →
            </Link>
            <Link
              to="/admin/triage"
              className="hidden md:inline-flex text-sm text-[#888680] hover:text-[#d9fb06] font-medium"
              data-testid="admin-nav-triage"
            >
              Triage Submissions →
            </Link>
            <span className="text-sm text-[#888680]">
              Welcome, <span className="text-[#dfddd6] font-medium">{adminUser.username}</span>
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="border-[#3f4816] text-[#888680] hover:text-[#d9fb06]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#d9fb06]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#d9fb06]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#dfddd6]">{stats.total}</p>
                  <p className="text-xs text-[#888680]">Total Cases</p>
                </div>
              </div>
            </div>
            <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#dfddd6]">{stats.recentWeek}</p>
                  <p className="text-xs text-[#888680]">This Week</p>
                </div>
              </div>
            </div>
            <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#dfddd6]">{stats.byStatus?.resolved || 0}</p>
                  <p className="text-xs text-[#888680]">Resolved</p>
                </div>
              </div>
            </div>
            <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#dfddd6]">
                    {(stats.byStatus?.submitted || 0) + (stats.byStatus?.under_review || 0)}
                  </p>
                  <p className="text-xs text-[#888680]">Pending</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888680]" />
              <Input
                placeholder="Search by Case ID, name, email..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6]"
                data-testid="admin-search"
              />
            </div>
            
            {/* Filter Toggle */}
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className="border-[#3f4816] text-[#888680] hover:text-[#d9fb06]"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </Button>
            
            {/* Refresh */}
            <Button
              onClick={() => { fetchCases(); fetchStats(); }}
              variant="outline"
              className="border-[#3f4816] text-[#888680] hover:text-[#d9fb06]"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            
            {/* Export */}
            <Button
              onClick={exportToCSV}
              variant="outline"
              className="border-[#3f4816] text-[#888680] hover:text-[#d9fb06]"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t border-[#3f4816]/50">
              <div className="w-48">
                <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                  <SelectTrigger className="bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6]">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                    <SelectItem value="all">All Statuses</SelectItem>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Select value={filters.platform || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, platform: v === "all" ? "" : v }))}>
                  <SelectTrigger className="bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6]">
                    <SelectValue placeholder="All Platforms" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                    <SelectItem value="all">All Platforms</SelectItem>
                    {PLATFORMS.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={() => setFilters({ status: '', platform: '', search: '' })}
                variant="ghost"
                size="sm"
                className="text-[#888680] hover:text-[#d9fb06]"
              >
                Clear Filters
              </Button>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedCases.length > 0 && (
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[#3f4816]/50">
              <span className="text-sm text-[#d9fb06] font-medium">
                {selectedCases.length} selected
              </span>
              <div className="w-48">
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6]">
                    <SelectValue placeholder="New Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="Note (optional)"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                className="w-48 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6]"
              />
              <Button
                onClick={handleBulkStatusUpdate}
                className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06]/90"
              >
                Update Selected
              </Button>
              <Button
                onClick={() => setSelectedCases([])}
                variant="ghost"
                size="sm"
                className="text-[#888680]"
              >
                Clear Selection
              </Button>
            </div>
          )}
        </div>

        {/* Cases Table */}
        <div className="bg-[#302f2c] border border-[#3f4816]/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1a1c1b]">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedCases.length === cases.length && cases.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-[#3f4816]"
                    />
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-[#888680] uppercase cursor-pointer hover:text-[#d9fb06]"
                    onClick={() => handleSort('caseId')}
                  >
                    Case ID {sortConfig.field === 'caseId' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-[#888680] uppercase cursor-pointer hover:text-[#d9fb06]"
                    onClick={() => handleSort('clientName')}
                  >
                    Client {sortConfig.field === 'clientName' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#888680] uppercase">
                    Platform
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#888680] uppercase">
                    Status
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-medium text-[#888680] uppercase cursor-pointer hover:text-[#d9fb06]"
                    onClick={() => handleSort('createdAt')}
                  >
                    Created {sortConfig.field === 'createdAt' && (sortConfig.order === 'desc' ? '↓' : '↑')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#888680] uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3f4816]/30">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <RefreshCw className="w-6 h-6 text-[#d9fb06] animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : cases.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-[#888680]">
                      No cases found
                    </td>
                  </tr>
                ) : (
                  cases.map(caseItem => {
                    const statusConfig = STATUS_CONFIG[caseItem.status] || STATUS_CONFIG.submitted;
                    return (
                      <tr key={caseItem.caseId} className="hover:bg-[#3f4816]/10">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedCases.includes(caseItem.caseId)}
                            onChange={() => handleSelectCase(caseItem.caseId)}
                            className="rounded border-[#3f4816]"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-sm text-[#d9fb06]">{caseItem.caseId}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm text-[#dfddd6] font-medium">{caseItem.clientName}</p>
                            <p className="text-xs text-[#888680]">{caseItem.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-[#dfddd6]">{caseItem.platform}</span>
                        </td>
                        <td className="px-4 py-3">
                          {editingCase === caseItem.caseId ? (
                            <div className="flex items-center gap-2">
                              <Select value={newStatus} onValueChange={setNewStatus}>
                                <SelectTrigger className="w-36 h-8 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] text-xs">
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent className="bg-[#302f2c] border-[#3f4816]">
                                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                    <SelectItem key={key} value={key} className="text-xs">{config.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                onClick={() => handleUpdateSingleStatus(caseItem.caseId)}
                                className="h-8 bg-[#d9fb06] text-[#1a1c1b]"
                              >
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setEditingCase(null); setNewStatus(''); }}
                                className="h-8 text-[#888680]"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ) : (
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}/20 ${statusConfig.textColor}`}>
                              {statusConfig.label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-[#888680]">{formatDate(caseItem.createdAt)}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => { setEditingCase(caseItem.caseId); setNewStatus(caseItem.status); }}
                              className="h-8 w-8 p-0 text-[#888680] hover:text-[#d9fb06]"
                              title="Edit Status"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => window.open(`/track-case?id=${caseItem.caseId}`, '_blank')}
                              className="h-8 w-8 p-0 text-[#888680] hover:text-[#d9fb06]"
                              title="View Case"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-[#3f4816]/50 flex items-center justify-between">
              <span className="text-sm text-[#888680]">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  className="border-[#3f4816] text-[#888680]"
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  className="border-[#3f4816] text-[#888680]"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboardPage;
